/**
 * DownloadLink Web Component
 * 
 * A download link with automatic file type icons.
 * 
 * Usage:
 *   <download-link file="documents/report.pdf">
 *     Quarterly Report
 *   </download-link>
 * 
 * Attributes:
 *   - file: Path to the file (required)
 *   - alt: Alt text for the icon (default: auto-generated)
 *   - grid: Display in grid layout (boolean attribute)
 *   - icon-size: Size of the icon (default: "1cm")
 *   - target: Link target (default: "_blank")
 */

import { getIconSvg, getSupportedExtensions } from './icons.js';

const STYLES = `
  :host {
    display: inline;
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
  }
  
  :host(.ready) {
    visibility: visible;
    opacity: 1;
  }
  
  .container {
    display: inline-flex;
    align-items: center;
    gap: 0.5em;
    text-decoration: none;
    color: inherit;
    cursor: pointer;
  }
  
  .container:hover {
    text-decoration: underline;
  }
  
  .container.grid {
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: 6pt;
    align-items: start;
  }
  
  .icon {
    flex-shrink: 0;
    width: var(--icon-size, 1cm);
    height: var(--icon-size, 1cm);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .icon svg {
    width: 100%;
    height: 100%;
  }
  
  .label {
    line-height: 1.4;
  }
  
  @media (max-width: 600px) {
    .container {
      display: grid;
      grid-template-columns: auto 1fr;
      column-gap: 6pt;
    }
  }
  
  /* Touch device adjustments */
  @media (hover: none) {
    .container:hover {
      text-decoration: none;
    }
    
    .container:active {
      text-decoration: underline;
    }
  }
`;

class DownloadLink extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._extension = 'default';
  }
  
  static get observedAttributes() {
    return ['file', 'alt', 'grid', 'icon-size', 'target'];
  }
  
  connectedCallback() {
    // Extract extension from file attribute BEFORE rendering
    const file = this.getAttribute('file');
    if (file) {
      this._extension = file.split('.').pop().toLowerCase();
    }
    
    this._render();
    this._updateIcon();
    this._updateLink();
    
    queueMicrotask(() => {
      this.classList.add('ready');
    });
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    
    if (name === 'file' && newValue) {
      this._extension = newValue.split('.').pop().toLowerCase();
      if (this.isConnected) {
        this._updateIcon();
        this._updateLink();
      }
    }
    
    if (name === 'icon-size' && this.isConnected) {
      this.shadowRoot.host.style.setProperty('--icon-size', newValue);
    }
    
    if (name === 'grid' && this.isConnected) {
      const container = this.shadowRoot.querySelector('.container');
      container?.classList.toggle('grid', this.hasAttribute('grid'));
    }
    
    if (name === 'alt' && this.isConnected) {
      this._updateIcon();
    }
    
    if (name === 'target' && this.isConnected) {
      this._updateLink();
    }
  }
  
  _render() {
    const iconSize = this.getAttribute('icon-size');
    const styleVar = iconSize ? `--icon-size: ${iconSize};` : '';
    const gridClass = this.hasAttribute('grid') ? ' grid' : '';
    
    this.shadowRoot.innerHTML = `
      <style>${STYLES}</style>
      <a class="container${gridClass}" style="${styleVar}" target="${this.getAttribute('target') || '_blank'}">
        <span class="icon"></span>
        <span class="label"><slot></slot></span>
      </a>
    `;
    
    // Set up click handler
    const link = this.shadowRoot.querySelector('a');
    link.addEventListener('click', (e) => this._handleClick(e));
  }
  
  _updateIcon() {
    const iconContainer = this.shadowRoot.querySelector('.icon');
    if (!iconContainer) return;
    
    const svg = getIconSvg(this._extension);
    iconContainer.innerHTML = svg;
    
    // Set alt/title
    const alt = this.getAttribute('alt') || `Download ${this._extension.toUpperCase()} file`;
    iconContainer.setAttribute('title', alt);
    iconContainer.setAttribute('role', 'img');
    iconContainer.setAttribute('aria-label', alt);
  }
  
  _updateLink() {
    const link = this.shadowRoot.querySelector('a');
    if (!link) return;
    
    const file = this.getAttribute('file');
    if (file) {
      const encodedPath = encodeURIComponent(file).replaceAll('%2F', '/');
      link.href = encodedPath;
    }
    
    link.target = this.getAttribute('target') || '_blank';
  }
  
  _handleClick(e) {
    const file = this.getAttribute('file');
    if (!file) {
      e.preventDefault();
      console.warn('DownloadLink: No file attribute specified');
      return;
    }
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('download-click', {
      bubbles: true,
      detail: {
        file: file,
        extension: this._extension
      }
    }));
  }
  
  // === Public API ===
  
  /**
   * Get the file path
   * @returns {string|null}
   */
  get file() {
    return this.getAttribute('file');
  }
  
  /**
   * Set the file path
   * @param {string} value
   */
  set file(value) {
    this.setAttribute('file', value);
  }
  
  /**
   * Get the file extension
   * @returns {string}
   */
  get extension() {
    return this._extension;
  }
  
  /**
   * Get list of supported file extensions
   * @returns {string[]}
   */
  static getSupportedExtensions() {
    return getSupportedExtensions();
  }
}

customElements.define('download-link', DownloadLink);

export { DownloadLink };
export default DownloadLink;
